/**
 * configuration.service.ts
 * Copyright © 2016-2018, My Coverage Plan, Inc.
 * All rights reserved.
 */

import { APP_INITIALIZER, FactoryProvider, Injectable } from "@angular/core";
import { Http, Response } from "@angular/http";
import {
    Configuration as GenericConfiguration,
    ClientConfiguration,
    ProtocolConfiguration,
    ReportConfiguration,
    SettingConfiguration,
    AnyConfig
} from "./configuration.types";

import { BehaviorSubject } from "rxjs/Rx";

type ConfigName = "client" | "protocol" | "reports" | "settings";

@Injectable()
export class ConfigurationService
{
    private map : Map<ConfigName, AnyConfig> =
        new Map<ConfigName, AnyConfig>();

    public get client () : ClientConfiguration
    {
        return this.map.get("client") as ClientConfiguration;
    }
    public get protocol () : ProtocolConfiguration
    {
        return this.map.get("protocol") as ProtocolConfiguration;
    }
    public get reports () : ReportConfiguration
    {
        return this.map.get("reports") as ReportConfiguration;
    }
    public get settings () : SettingConfiguration
    {
        return this.map.get("settings") as SettingConfiguration;
    }

    public debugSet (category: ConfigName, key : keyof AnyConfig, value : any) {
        (this.map.get(category) as GenericConfiguration)[key] = value;
    }

    public loaded : BehaviorSubject<boolean> = new BehaviorSubject(false);
    public ready : BehaviorSubject<boolean> = new BehaviorSubject(false);

    constructor (protected http : Http)
    {
        console.group(`App initialization`);
        this.retrieve();
        this.loaded.first((loaded : boolean) => loaded).subscribe(
            () => this.process());
        this.ready.subscribe(() => console.groupEnd());
        (window as any).__RECONFIG = this.debugSet.bind(this);
    }

    private validateLoad () : boolean
    {
        if (this.map.size === ConfigurationService.CONFIG_RESOURCES.length)
        {
            this.loaded.next(true);
            return true;
        }
        else
        {
            this.loaded.next(false);
            return false;
        }
    }

    public static CONFIG_RESOURCES : Array<[ConfigName, string]> = [
        ["client",      "/zone/config/client.json"],
        ["protocol",    "/static/config/protocol.json"],
        ["reports",     "/zone/config/reports.json"],
        ["settings",    "/static/config/settings.json"]
    ];

    private retrieve () : void
    {
        ConfigurationService.CONFIG_RESOURCES.forEach(
            ([name, resource] : [ConfigName, string]) =>
                this.http.get(resource).subscribe((r : Response) => {
                    const json : any = r.json();
                    const count : number = Object.keys(json).length;
                    this.map.set(name, json);
                    console.info(
                        `Loaded ${name} configuration (${count} entries)`);
                    if (this.validateLoad())
                    {
                        console.info(`Loading complete`);
                    }
                })
        );
    }

    private process () : void
    {
        try
        {
            this.addBrandInjections();
            if (this.client.allowParameterizedStyles)
            {
                this.swapSubstyle();
            }
            this.ready.next(true);
        }
        catch (err)
        {
            this.ready.error(err);
        }
    }

    public injectedTags : HTMLElement[] = [];
    private addBrandInjections () : void
    {
        if (Array.isArray(this.client.cssInjections))
        {
            this.client.cssInjections.forEach((href : string) => {
                const e : HTMLLinkElement = new HTMLLinkElement();
                e.href = href;
                e.rel = "stylesheet";
                document.head.appendChild(e);
                this.injectedTags.push(e);
            });
            console.info(this.client.cssInjections.length
                + "custom stylesheet(s) appended");
        }
        if (Array.isArray(this.client.jsInjections))
        {
            this.client.jsInjections.forEach((src : string) => {
                console.warn(`Cannot inject ${src}, scripts not permitted`);
                // const e : HTMLScriptElement = new HTMLScriptElement();
                // e.src = src;
                // document.body.appendChild(e);
                // this.injectedTags.push(e);
            });
        }
    }
    private swapSubstyle () : void
    {
        const brandStyles : HTMLLinkElement =
            document.getElementById("brand-styles") as HTMLLinkElement;
        if (!brandStyles)
        {
            throw new Error("Could not find link element for primary styles");
        }
        // ... TODO
    }

    public done () : Promise<boolean>
    {
        // Note we don't provide a reject case!!! The app won't even start
        // if any of the configurations won't load... (which is desirable IMO)
        return new Promise((resolve : (b : boolean) => void) =>
            this.ready.first((ready : boolean) => ready)
                .subscribe(() => resolve(true))
        );
    }
}

export const CONFIG_INITIALIZER : FactoryProvider = {
    provide: APP_INITIALIZER,
    useFactory: (config : ConfigurationService) => () => config.done(),
    deps: [ConfigurationService],
    multi: true
};



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/configuration/configuration.service.ts